import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Tabs,
  Space,

  Typography,
  Tag,
  Modal,
  Select,
  InputNumber,
  Tooltip,
  App
} from 'antd'
import {
  SettingOutlined,
  SearchOutlined,
  GlobalOutlined,
  ReloadOutlined,
  SaveOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { proxySettingsApi } from '../../services/api'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input


interface ProxySetting {
  id: number
  setting_key: string
  setting_value: string
  setting_type: 'boolean' | 'string' | 'number' | 'json' | 'array'
  category: string
  description: string
  is_active: boolean
  parsed_value: any
}

interface Category {
  category: string
  setting_count: number
}

const ProxySettings: React.FC = () => {
  const { message: messageApi } = App.useApp()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState<ProxySetting[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [activeCategory, setActiveCategory] = useState<string>('search')
  const [resetModalVisible, setResetModalVisible] = useState(false)
  const [pushStatus, setPushStatus] = useState<'success' | 'error' | 'pending' | null>(null)
  const [formValues, setFormValues] = useState<any>({})

  // 获取所有设置
  const fetchSettings = async () => {
    try {
      setLoading(true)
      const response = await proxySettingsApi.getAllSettings()
      if (response.success) {
        setSettings(response.data)
        
        // 设置表单初始值
        const formValues: any = {}
        response.data.forEach((setting: ProxySetting) => {
          formValues[setting.setting_key] = setting.parsed_value
        })
        form.setFieldsValue(formValues)
        setFormValues(formValues)
      }
    } catch (error) {
      console.error('获取设置失败:', error)
      messageApi.error('获取设置失败')
    } finally {
      setLoading(false)
    }
  }

  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const response = await proxySettingsApi.getCategories()
      if (response.success) {
        setCategories(response.data)
      }
    } catch (error) {
      console.error('获取分类失败:', error)
    }
  }

  // 保存设置
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // 构建更新数据
      const updateData = Object.keys(values).map(key => ({
        settingKey: key,
        settingValue: values[key]
      }))

      const response = await proxySettingsApi.batchUpdateSettings(updateData)
      if (response.success) {
        messageApi.success('设置保存成功，配置已自动推送到代理服务')
        setPushStatus('success')
        fetchSettings() // 重新获取设置
        // 3秒后清除状态
        setTimeout(() => setPushStatus(null), 3000)
      } else {
        messageApi.error('保存失败: ' + response.error)
        setPushStatus('error')
        setTimeout(() => setPushStatus(null), 3000)
      }
    } catch (error) {
      console.error('保存设置失败:', error)
      messageApi.error('保存设置失败')
    } finally {
      setLoading(false)
    }
  }

  // 手动推送配置到代理
  const handlePushToProxy = async () => {
    try {
      setLoading(true)
      setPushStatus('pending')
      const response = await proxySettingsApi.pushToProxy()
      if (response.success) {
        messageApi.success('配置已成功推送到代理服务')
        setPushStatus('success')
        // 3秒后清除状态
        setTimeout(() => setPushStatus(null), 3000)
      } else {
        messageApi.error('推送失败: ' + response.error)
        setPushStatus('error')
        setTimeout(() => setPushStatus(null), 3000)
      }
    } catch (error) {
      console.error('推送配置失败:', error)
      messageApi.error('推送配置失败')
      setPushStatus('error')
      setTimeout(() => setPushStatus(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  // 重置设置
  const handleReset = async () => {
    try {
      setLoading(true)
      const response = await proxySettingsApi.resetToDefaults({
        category: activeCategory === 'all' ? undefined : activeCategory
      })
      if (response.success) {
        messageApi.success('设置已重置为默认值')
        fetchSettings()
        setResetModalVisible(false)
      } else {
        messageApi.error('重置失败: ' + response.error)
      }
    } catch (error) {
      console.error('重置设置失败:', error)
      messageApi.error('重置设置失败')
    } finally {
      setLoading(false)
    }
  }

  // 🔥 新增：设置项中文名称映射
  const getSettingDisplayName = (settingKey: string): string => {
    const nameMap: Record<string, string> = {
      // 搜索功能设置
      'search.global_enabled': '启用全局搜索',
      'search.keywords_enabled': '启用关键词检测',
      'search.keywords_list': '搜索关键词列表',
      'search.prompt_detection': '启用提示词检测',
      'search.tool_detection': '启用工具检测',
      'search.mode': '搜索模式',
      'search.deep_enabled': '启用深入搜索',
      'search.skill_name': '搜索技能名称 (建议使用默认值)',

      // 通用设置
      'general.log_level': '日志级别',
      'general.max_retries': '最大重试次数',
      'general.timeout': '请求超时时间(毫秒)',

      // 其他设置
      'auth.enabled': '启用认证',
      'auth.token': '认证令牌',
      'cache.enabled': '启用缓存',
      'cache.ttl': '缓存过期时间',
      'rate_limit.enabled': '启用限流',
      'rate_limit.max_requests': '最大请求数',
      'rate_limit.window_ms': '限流窗口时间'
    }

    return nameMap[settingKey] || settingKey.split('.').pop() || settingKey
  }

  // 🔥 新增：设置项中文描述映射
  const getSettingDescription = (settingKey: string, originalDescription: string): string => {
    const descMap: Record<string, string> = {
      'search.global_enabled': '🔥 新增功能：控制是否启用全局搜索功能。开启后支持两种模式：1）仅全局搜索（推荐）- 让refly.ai自己判断是否需要搜索；2）全局搜索+关键词检测 - 使用关键词触发搜索。关闭后所有搜索相关功能都将停用。',
      'search.keywords_enabled': '控制是否启用关键词检测模式。开启时：只有包含搜索关键词的查询才会触发搜索；关闭时：所有查询都会发送给refly.ai，由AI智能判断是否需要搜索（推荐模式）。',
      'search.keywords_list': '定义触发搜索功能的关键词列表，支持中英文关键词。格式为JSON数组，例如：["搜索","查找","天气","新闻","search","find"]。仅在启用关键词检测时生效。',
      'search.prompt_detection': '控制是否启用提示词检测，用于识别用户的搜索指令。配合关键词检测使用，可以更精确地识别搜索意图。',
      'search.tool_detection': '控制是否启用工具检测，用于识别需要调用搜索工具的场景。高级功能，一般情况下保持关闭即可。',
      'search.mode': '设置搜索模式：webSearch(普通搜索，快速返回基本信息) 或 deepSearch(深入搜索，提供更详细的分析和结果)。建议根据使用场景选择。',
      'search.deep_enabled': '控制是否启用深入搜索功能。开启后会使用deepSearch模式，提供更详细的搜索结果和分析，但响应时间会稍长。',
      'search.skill_name': '设置搜索功能使用的AI技能名称。默认值：commonQnA（推荐，适用于大多数场景）。其他选项：customPrompt（自定义提示词场景）。⚠️ 建议保持默认设置，除非有特殊需求。',
      'general.log_level': '设置系统日志级别。debug：显示所有日志（开发调试用）；info：显示一般信息；warn：仅显示警告；error：仅显示错误。生产环境建议使用info或warn。',
      'general.max_retries': '设置请求失败时的最大重试次数。范围：0-10次。设置过高可能导致响应延迟，设置过低可能影响稳定性。建议值：3-5次。',
      'general.timeout': '设置请求的超时时间，单位为毫秒。范围：5000-60000ms。设置过短可能导致请求超时，设置过长可能影响用户体验。建议值：15000-30000ms。'
    }

    return descMap[settingKey] || originalDescription
  }

  // 渲染设置项
  const renderSettingItem = (setting: ProxySetting) => {
    const { setting_key, setting_type, description, parsed_value } = setting
    const displayName = getSettingDisplayName(setting_key)
    const displayDescription = getSettingDescription(setting_key, description)

    switch (setting_type) {
      case 'boolean':
        return (
          <Form.Item
            key={setting_key}
            name={setting_key}
            label={
              <Space>
                <Text strong>{displayName}</Text>
                <Tooltip title={displayDescription}>
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        )

      case 'number':
        return (
          <Form.Item
            key={setting_key}
            name={setting_key}
            label={
              <Space>
                <Text strong>{displayName}</Text>
                <Tooltip title={displayDescription}>
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'json':
      case 'array':
        return (
          <Form.Item
            key={setting_key}
            name={setting_key}
            label={
              <Space>
                <Text strong>{displayName}</Text>
                <Tooltip title={displayDescription}>
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  try {
                    if (typeof value === 'string') {
                      JSON.parse(value)
                    }
                    return Promise.resolve()
                  } catch {
                    return Promise.reject(new Error('请输入有效的JSON格式'))
                  }
                }
              }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请输入JSON格式的数据"
              value={typeof parsed_value === 'object' ? JSON.stringify(parsed_value, null, 2) : parsed_value}
              onChange={(e) => {
                form.setFieldValue(setting_key, e.target.value)
              }}
            />
          </Form.Item>
        )

      default:
        // 🔥 特殊处理：搜索模式使用下拉选择器
        if (setting_key === 'search.mode') {
          return (
            <Form.Item
              key={setting_key}
              name={setting_key}
              label={
                <Space>
                  <Text strong>{displayName}</Text>
                  <Tooltip title={displayDescription}>
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              }
            >
              <Select
                placeholder="请选择搜索模式"
                options={[
                  { value: 'webSearch', label: '普通搜索 (webSearch)' },
                  { value: 'deepSearch', label: '深入搜索 (deepSearch)' }
                ]}
              />
            </Form.Item>
          )
        }

        // 🔥 特殊处理：搜索技能名称使用下拉选择器
        if (setting_key === 'search.skill_name') {
          return (
            <Form.Item
              key={setting_key}
              name={setting_key}
              label={
                <Space>
                  <Text strong>{displayName}</Text>
                  <Tooltip title={displayDescription}>
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              }
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  💡 建议使用默认值 commonQnA，除非有特殊需求。customPrompt 用于自定义提示词场景
                </Text>
              }
            >
              <Select
                placeholder="请选择搜索技能"
                allowClear
                options={[
                  { value: 'commonQnA', label: 'commonQnA (推荐默认值)', disabled: false },
                  { value: 'customPrompt', label: 'customPrompt (自定义提示词)', disabled: false },
                  { value: 'CommonQnA', label: 'CommonQnA (兼容格式)', disabled: false }
                ]}
              />
            </Form.Item>
          )
        }

        return (
          <Form.Item
            key={setting_key}
            name={setting_key}
            label={
              <Space>
                <Text strong>{displayName}</Text>
                <Tooltip title={displayDescription}>
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input />
          </Form.Item>
        )
    }
  }

  // 按分类过滤设置
  const getSettingsByCategory = (category: string) => {
    if (category === 'all') return settings
    return settings.filter(setting => setting.category === category)
  }

  // 监听表单值变化
  const handleFormValuesChange = (_: any, allValues: any) => {
    setFormValues(allValues)
  }

  useEffect(() => {
    fetchSettings()
    fetchCategories()
  }, [])

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <SettingOutlined /> 代理服务配置
          </Title>
          <Paragraph type="secondary">
            管理 dev/ser/proxy.js 的配置项，实时控制代理服务的行为和功能开关
          </Paragraph>

          <Card size="small" style={{ marginTop: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
              <QuestionCircleOutlined /> 功能说明
            </Title>
            <div style={{ marginTop: '12px' }}>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>🔥 搜索模式选择：</Text>
                <Text>支持两种搜索模式：</Text>
                <br />
                <Text style={{ marginLeft: '16px' }}>• <Text strong>模式1（推荐）</Text>：仅开启全局搜索，让refly.ai智能判断是否需要搜索</Text>
                <br />
                <Text style={{ marginLeft: '16px' }}>• <Text strong>模式2</Text>：全局搜索+关键词检测，使用关键词触发搜索</Text>
              </Paragraph>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>🔍 搜索功能控制：</Text> 控制代理服务是否启用智能搜索检测。全局搜索是总开关，关闭后所有搜索功能停用。
              </Paragraph>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>📝 关键词管理：</Text> 自定义搜索触发关键词列表，支持中英文关键词。仅在启用关键词检测时生效，用于精确控制搜索触发时机。
              </Paragraph>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>🎯 搜索深度：</Text> 普通搜索快速返回基本信息，深入搜索提供详细分析但响应时间较长。根据需求选择合适的搜索模式。
              </Paragraph>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>⚙️ 系统参数：</Text> 配置日志级别、重试次数、超时时间等系统运行参数，影响代理服务的性能和稳定性。
              </Paragraph>
              <Paragraph style={{ margin: '8px 0' }}>
                <Text strong>🎯 技能配置：</Text> 设置搜索功能使用的技能名称，建议使用默认值commonQnA，除非有特殊需求。
              </Paragraph>
              <Paragraph style={{ margin: '8px 0', color: '#fa8c16' }}>
                <Text strong>⚠️ 注意：</Text> 修改配置后点击"推送到代理"立即生效，影响所有正在运行的代理请求，请谨慎操作。
              </Paragraph>
            </div>
          </Card>

          {/* 🔥 新增：搜索功能快速配置面板 */}
          {activeCategory === 'search' && (
            <Card
              size="small"
              style={{ marginTop: '16px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}
              title={
                <Space>
                  <SearchOutlined style={{ color: '#1890ff' }} />
                  <Text strong style={{ color: '#1890ff' }}>搜索功能快速配置</Text>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Text strong>当前搜索模式：</Text>
                  <Tag color={formValues['search.mode'] === 'deepSearch' ? 'purple' : 'blue'}>
                    {formValues['search.mode'] === 'deepSearch' ? '深入搜索' : '普通搜索'}
                  </Tag>
                  <Text type="secondary">
                    {formValues['search.mode'] === 'deepSearch'
                      ? '提供更详细的搜索结果和分析，响应时间较长'
                      : '快速搜索，返回基本信息，响应速度快'}
                  </Text>
                </Space>

                <Space>
                  <Text strong>工作模式：</Text>
                  {formValues['search.global_enabled'] && !formValues['search.keywords_enabled'] && (
                    <Tag color="green">模式1 - 智能搜索（推荐）</Tag>
                  )}
                  {formValues['search.global_enabled'] && formValues['search.keywords_enabled'] && (
                    <Tag color="blue">模式2 - 关键词触发搜索</Tag>
                  )}
                  {!formValues['search.global_enabled'] && (
                    <Tag color="red">搜索功能已禁用</Tag>
                  )}
                  <Text type="secondary">
                    {formValues['search.global_enabled'] && !formValues['search.keywords_enabled']
                      ? '让refly.ai智能判断是否需要搜索，适用于所有查询'
                      : formValues['search.global_enabled'] && formValues['search.keywords_enabled']
                      ? '只有包含搜索关键词的查询才会触发搜索'
                      : '所有搜索相关功能都已停用'}
                  </Text>
                </Space>

                <Space>
                  <Text strong>功能状态：</Text>
                  <Tag color={formValues['search.global_enabled'] ? 'green' : 'red'}>
                    全局搜索：{formValues['search.global_enabled'] ? '已启用' : '已禁用'}
                  </Tag>
                  <Tag color={formValues['search.keywords_enabled'] ? 'green' : 'red'}>
                    关键词检测：{formValues['search.keywords_enabled'] ? '已启用' : '已禁用'}
                  </Tag>
                  <Tag color={formValues['search.prompt_detection'] ? 'green' : 'red'}>
                    提示词检测：{formValues['search.prompt_detection'] ? '已启用' : '已禁用'}
                  </Tag>
                </Space>

                {/* 🔥 新增：配置建议 */}
                <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bae7ff' }}>
                  <Text strong style={{ color: '#1890ff' }}>💡 配置建议：</Text>
                  <br />
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    • <Text strong>新用户</Text>：推荐使用模式1（仅开启全局搜索），简单易用
                    <br />
                    • <Text strong>精确控制</Text>：使用模式2（全局搜索+关键词检测），可控制搜索触发时机
                    <br />
                    • <Text strong>高级用户</Text>：可开启深入搜索获得更详细的分析结果
                  </Text>
                </div>
              </Space>
            </Card>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={loading}
            >
              保存设置
            </Button>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={handlePushToProxy}
              loading={loading}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
            >
              推送到代理
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setResetModalVisible(true)}
            >
              重置为默认值
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchSettings}
              loading={loading}
            >
              刷新
            </Button>

            {/* 🔥 新增：推送状态指示器 */}
            {pushStatus && (
              <Tag
                color={
                  pushStatus === 'success' ? 'green' :
                  pushStatus === 'error' ? 'red' :
                  'blue'
                }
                style={{ marginLeft: '8px' }}
              >
                {pushStatus === 'success' ? '✅ 推送成功' :
                 pushStatus === 'error' ? '❌ 推送失败' :
                 '⏳ 推送中...'}
              </Tag>
            )}
          </Space>
        </div>

        <Tabs
          activeKey={activeCategory}
          onChange={setActiveCategory}
          items={[
            {
              key: 'all',
              label: (
                <Space>
                  <GlobalOutlined />
                  全部设置
                  <Tag color="blue">{settings.length}</Tag>
                </Space>
              ),
            },
            ...categories.map(cat => ({
              key: cat.category,
              label: (
                <Space>
                  {cat.category === 'search' && <SearchOutlined />}
                  {cat.category === 'general' && <SettingOutlined />}
                  {cat.category === 'search' ? '搜索功能' :
                   cat.category === 'general' ? '通用设置' : cat.category}
                  <Tag color="green">{cat.setting_count}</Tag>
                </Space>
              ),
            }))
          ]}
        />

        <div style={{ marginTop: '24px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            name="proxySettingsForm"
            onValuesChange={handleFormValuesChange}
          >
            {getSettingsByCategory(activeCategory).map(renderSettingItem)}
          </Form>
        </div>
      </Card>

      <Modal
        title="重置设置"
        open={resetModalVisible}
        onOk={handleReset}
        onCancel={() => setResetModalVisible(false)}
        confirmLoading={loading}
      >
        <p>
          确定要重置
          {activeCategory === 'all' ? '所有设置' : `${activeCategory} 分类的设置`}
          为默认值吗？此操作不可撤销。
        </p>
      </Modal>
    </div>
  )
}

export default ProxySettings
